import { ColorPalettes } from '@/features/reservation/constants/ColorPalettes';
import { Box, SxProps, Tab, TabProps, Tabs, TabsProps, Theme, Typography, TypographyProps } from '@mui/material';
import React, { useState } from 'react';

type TabsType = {
  component: JSX.Element;
  label: string;
};
interface TabComponentProps {
  tabs: TabsType[];
  defaultValue?: number;
  tabsProps?: TabsProps;
  tabProps?: TabProps;
  selectTabProps?: TabProps;
  labelProps?: TypographyProps;
  selectLabelProps?: TypographyProps;
}

const tabIdPrefix = 'simple-tabpanel-';

const sxTabDefault: SxProps<Theme> = { width: '50%', maxWidth: 'none' };

export const defaultTabComponentProps: TabComponentProps = {
  tabs: [],
  defaultValue: 0,
  tabsProps: {
    className: 'flex relative rounded',
    TabIndicatorProps: {
      sx: {
        height: 0
      }
    },
    sx: { border: 1, borderColor: ColorPalettes.MAIN }
  },
  tabProps: {
    sx: sxTabDefault
  },
  selectTabProps: {
    className: 'bg-primary',
    sx: sxTabDefault
  },
  labelProps: { className: 'text-primary' },
  selectLabelProps: { sx: { color: ColorPalettes.WHITE } }
};

export const mypageTabComponentProps: TabComponentProps = {
  tabs: [],
  defaultValue: 0,
  tabsProps: {
    className: 'flex relative',
    TabIndicatorProps: {
      sx: {
        height: 0
      }
    },
    sx: { minHeight: 'auto' }
  },
  tabProps: {
    sx: { ...sxTabDefault, py: 0.5, minHeight: 'auto' }
  },
  selectTabProps: {
    sx: { ...sxTabDefault, py: 0.5, minHeight: 'auto', borderBottom: 4, borderColor: ColorPalettes.MAIN }
  },
  labelProps: { className: 'font-bold', sx: { opacity: 0.5, minHeight: 10 } },
  selectLabelProps: { className: 'font-bold', sx: { color: ColorPalettes.GRAY } }
};

/**
 * タブコンポーネント
 * @param tabs タブ
 * @param defaultValue デフォルトタブ
 * @param tabsProps tabsProps
 * @param tabProps tabProps
 * @param selectTabProps selectTabProps
 * @param labelProps labelProps
 * @param selectLabelProps selectLabelProps
 * @returns タブコンポーネント
 */
export function TabComponent({
  tabs = defaultTabComponentProps.tabs,
  defaultValue = defaultTabComponentProps.defaultValue,
  tabsProps = defaultTabComponentProps.tabsProps,
  tabProps = defaultTabComponentProps.tabProps,
  selectTabProps = defaultTabComponentProps.selectTabProps,
  labelProps = defaultTabComponentProps.labelProps,
  selectLabelProps = defaultTabComponentProps.selectLabelProps
}: TabComponentProps) {
  const [selected, setSelected] = useState<number | undefined>(defaultValue);
  if (!tabs.length) return undefined;

  return (
    <>
      <Tabs
        {...tabsProps}
        value={selected}
        onChange={(e, value) => {
          setSelected(value);
        }}
      >
        {tabs.map((tab, index) => {
          const isSelect = selected === index;
          return (
            <Tab
              key={`tab-${index}`}
              {...(isSelect ? selectTabProps : tabProps)}
              label={<Typography {...(isSelect ? selectLabelProps : labelProps)}>{tab.label}</Typography>}
              aria-controls={`${tabIdPrefix}${index}`}
            />
          );
        })}
      </Tabs>
      {tabs.map((tab, index) => (
        <TabPanel key={`tabPanel-${index}`} component={tab.component} index={index} selected={selected} />
      ))}
    </>
  );
}

interface TabPanelProps {
  component?: React.ReactNode;
  index: number;
  selected: number | undefined;
}

function TabPanel({ component: children, selected, index }: TabPanelProps) {
  const isSelect = selected === index;

  return (
    <Box role="tabpanel" hidden={!isSelect} id={`${tabIdPrefix}${index}`}>
      {isSelect && children}
    </Box>
  );
}
